import {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
} from 'react';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { MobileVizFooter } from '../../components/MobileVizFooter';
import _ from 'lodash';
import { useGetChartComponent } from '../../discovery/viz-chart/viz-chart.hook';
import { VizLegend } from '../../discovery/charts/viz-legend';
import { ActiveFilterPanel } from '../../discovery/filter/active-filter-panel';
import LoginActions from '../../common/redux/actions/LoginActions';
import RemoveFieldFromVizDialog from '../../discovery/RemoveFieldFromVizDialog';
import AddFieldToVizWarningDialog from '../../discovery/AddFieldToVizWarningDialog';
import QueryWarningDialog from '../../discovery/QueryWarningDialog';
import { QueryErrorDialog } from '../../discovery/query-error';
import {
  isNoAccess,
  isNonDashletUser,
} from '../../common/redux/selectors/AccountSelectors.js';
import { messages } from '../../i18n';
import { css } from '@emotion/react';
import { IDiscoverEmotionTheme, useDiscoverTheme } from '../../common/emotion';
import { useResizeDetector } from 'react-resize-detector';
import styled from '@emotion/styled';
import { useViewportDimensions } from '../../common/utilities/dimensions.hook';
import {
  InvisibleButton,
  PrimaryButton,
  SmallModalButtonContainer,
} from '../../ui';
import { IDiscovery, IViz } from '../../discovery';
import {
  CenteredPanel,
  CenteredPanelTitle,
  ChartErrorBoundary,
} from './report-dashlet.styles';
import { withDiscoverOption } from '../../discovery/discovery-context/discovery.context';
import { ErrorBoundaryComponentProps } from '../../components/error-boundary';
import { SkeletonVizLoader } from '../../common/loaders/skeleton-viz-loader';
import { ModalPortal } from '../../common/widgets/dialogs/modal-portal';
import { VIZ_OPTION_IDS } from '../../common';
import DashletActions from '../../common/redux/actions/DashletActions';
import { withSugarCoreDropdownColors } from '../../common/hoc/SugarCoreDropdownColors';
import { VizQueryHOC } from '../../discovery/charts/base-cartesian-chart';
import { VizChart } from '../../discovery/viz-chart';
import { LegacyVizChart } from '../../discovery/viz-chart/viz-chart.memo';

const StyledModal = styled(ModalPortal)(() =>
  css({
    margin: 0,
    position: 'absolute',

    '.modal-content': {
      paddingRight: '5px',
      border: 0,
      boxShadow: 'none',
    },
  }),
);

const OuterWrapper = ({
  children,
}: {
  width?: number;
  height?: number;
  children;
}) => {
  return (
    <div className='discover' style={{ height: '100%' }}>
      <div className='discover-content-panel' style={{ height: '100%' }}>
        <div className='discover-open-panel' style={{ height: '100%' }}>
          <div className='discover-tab-panel' style={{ height: '100%' }}>
            <div className='viz-container' style={{ height: '100%' }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface IPropTypes {
  loading?: boolean;
  vizId?: string;
  dashletId?: string;
  badId?: boolean;
  openDiscovery: (discovery: Partial<IDiscovery>) => void;
  reportViz: IViz;
  discovery?;
  viz?;
  advanced?: boolean;
  showReAuthenticate?: boolean;
  discoveriesLoading?: boolean;
  isLoggedIn?: boolean;
  isForcedMobile?: boolean;
  isDashletMode?: boolean;
  theme?: IDiscoverEmotionTheme;
  logout?;
  children?;
  isNoAccess: boolean;
  fetching: boolean;
  showLegendPanel: boolean;
  applyVizState?: (vizState: any) => void;
}

const ContentDiv = styled.div`
  min-height: 0px;
  min-width: 0px;
  overflow: hidden;
`;
const CenterDiv = styled.div`
  margin: auto;
  text-align: center;
`;

const Content = ({ vizId }) => {
  const {
    width: resizeWidth,
    height,
    ref,
  } = useResizeDetector({
    handleHeight: true,
    handleWidth: false,
  });
  const { width: viewportWidth } = useViewportDimensions();
  const width = _.min([resizeWidth, viewportWidth]);

  const ChartComponent = useGetChartComponent(vizId, VizChart, LegacyVizChart);

  return (
    <ContentDiv ref={ref} className='content'>
      <div
        className='viz-chart'
        style={{ width: width === 0 ? '100%' : width, height }}
      >
        <ChartComponent
          width={width}
          height={Math.max(height, 0)}
          vizId={vizId}
        />
      </div>
    </ContentDiv>
  );
};

const mapStateToProps = (state, props: any) => {
  const { vizId } = props;
  const {
    main: { showReAuthenticate, advanced, isForcedMobile } = {} as any,
    dashlet: { isDashletMode } = {} as any,
    discover: { openDiscoveries } = {} as any,
    account,
  } = state;
  const discovery = _.get(openDiscoveries, [vizId, 'present']);
  const { viz } = discovery || {};
  return {
    loading: !viz,
    viz,
    isLoggedIn: isNonDashletUser(account),
    isNoAccess: isNoAccess(account),
    showReAuthenticate,
    advanced,
    discovery,
    vizId,
    isForcedMobile,
    isDashletMode,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    logout: () => {
      LoginActions.logout(dispatch);
    },
    openDiscovery: (discovery: Partial<IDiscovery>) => {
      dispatch(DashletActions.openDiscovery(discovery, ownProps?.dashletId));
    },
  };
};

export const ReportDashlet: ComponentType<{
  vizId?: string;
  dashletId?: string;
  isDrillLinking?: boolean;
}> = compose(
  withDiscoverOption({ option: VIZ_OPTION_IDS.showLegendPanel }),
  connect(mapStateToProps, mapDispatchToProps),
  withSugarCoreDropdownColors,
  VizQueryHOC({
    withOtherPropsFunc: ({ visualization: viz, loading }) => {
      if (!loading && viz) {
        return {
          fetching: loading,
          reportViz: viz,
          discoveriesLoading: false,
        };
      } else {
        return { fetching: loading, discoveriesLoading: true };
      }
    },
  }),
)(
  memo(
    ({
      reportViz,
      vizId,
      loading,
      openDiscovery,
      discoveriesLoading,
      showReAuthenticate,
      viz,
      advanced,
      logout,
      isNoAccess,
      fetching,
      showLegendPanel,
    }: IPropTypes) => {
      const {
        viewWidth,
        viewHeight,
        colors: { PanelBorder },
      } = useDiscoverTheme();
      const [suppressErrorDialog, setSuppressErrorDialog] = useState(false);
      const [badId, setBadId] = useState(false);
      const [updating, setUpdating] = useState(false);

      useEffect(() => {
        if (!isNoAccess && loading) {
          if (!reportViz && !discoveriesLoading) {
            setBadId(true);
          } else if (reportViz && !updating) {
            setUpdating(true);
            setBadId(false);
            openDiscovery({ viz: reportViz });
          }
        } else {
          setUpdating(false);
          setBadId(false);
        }
      }, [
        loading,
        reportViz,
        discoveriesLoading,
        openDiscovery,
        updating,
        isNoAccess,
      ]);
      const hideErrorDialog = useCallback(
        () => setSuppressErrorDialog(true),
        [],
      );
      const errorMessage = useMemo(() => {
        if (isNoAccess) {
          return messages.formatString(
            messages.dashlet.noAccess,
            messages.nonTranslated.discover,
          );
        } else if (showReAuthenticate) {
          return messages.formatString(
            messages.dashlet.failedToAuthenticate,
            messages.nonTranslated.sugarDiscover,
          );
        } else if (!vizId) {
          return messages.formatString(
            messages.dashlet.dashletNotInitialized,
            messages.nonTranslated.dashlet,
          );
        } else if (badId) {
          return messages.formatString(
            messages.dashlet.reportNotFound,
            messages.nonTranslated.sugarDiscover,
            messages.nonTranslated.discover,
          );
        }
      }, [badId, showReAuthenticate, vizId, isNoAccess]);
      if (errorMessage) {
        if (isNoAccess) {
          return (
            <OuterWrapper width={viewWidth} height={viewHeight}>
              <CenterDiv>{errorMessage}</CenterDiv>
            </OuterWrapper>
          );
        } else {
          return (
            !suppressErrorDialog && (
              <OuterWrapper width={viewWidth} height={viewHeight}>
                <StyledModal
                  title={'Error loading report'}
                  buttonGroup={
                    <SmallModalButtonContainer>
                      <InvisibleButton onClick={hideErrorDialog}>
                        {messages.cancel}
                      </InvisibleButton>
                      <PrimaryButton onClick={logout}>Log out</PrimaryButton>
                    </SmallModalButtonContainer>
                  }
                >
                  <div className={'modal-content'}>
                    {errorMessage} Please logout and try again
                  </div>
                </StyledModal>
              </OuterWrapper>
            )
          );
        }
      } else if (
        loading &&
        ((fetching && discoveriesLoading) || (!fetching && !discoveriesLoading))
      ) {
        return (
          <>
            {advanced && (
              <div>
                Loading
                <pre>
                  {JSON.stringify({ fetching, loading, discoveriesLoading })}
                </pre>
              </div>
            )}
            <SkeletonVizLoader />
          </>
        );
      } else if (loading && discoveriesLoading && !fetching) {
        return (
          <CenteredPanel>
            <CenteredPanelTitle>
              {messages.dashlet.reportDeletedTitle}
            </CenteredPanelTitle>
            <p>{messages.dashlet.reportDeletedDescr}</p>
          </CenteredPanel>
        );
      }
      return (
        <OuterWrapper width={viewWidth} height={viewHeight}>
          <ActiveFilterPanel
            panelStyle={{
              height: 40,
              overflowX: 'auto',
              borderBottomColor: PanelBorder,
              borderBottomStyle: 'solid',
              borderBottomWidth: 1,
            }}
            discoveryId={vizId}
            viz={viz}
          />
          {showLegendPanel && (
            <VizLegend
              panelStyle={{
                height: 'auto',
                flexShrink: 0,
                flexGrow: 0,
                borderBottomColor: PanelBorder,
                borderBottomStyle: 'solid',
                borderBottomWidth: 1,
                borderLeftWidth: 0,
                borderTopWidth: 0,
              }}
              forceHorizontal={true}
              isResizable={false}
              vizId={vizId}
            />
          )}
          <ChartErrorBoundary {...ErrorBoundaryComponentProps}>
            <Content vizId={vizId} />
          </ChartErrorBoundary>
          <MobileVizFooter
            hideLabels={true}
            vizId={vizId}
            overrideStyle={{
              width: 'calc(100% - 2px)',
              margin: '0 1px 1px',
              height: '36px',
              borderTopColor: PanelBorder,
              zIndex: '80',
            }}
          />
          <RemoveFieldFromVizDialog />
          <AddFieldToVizWarningDialog />
          <QueryWarningDialog vizId={vizId} />
          <QueryErrorDialog vizId={vizId} />
        </OuterWrapper>
      );
    },
  ),
) as any;
